import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { KbvPluginService } from "@/services/kbvPluginService";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import DateChips from "./DateChips";
import LeagueGroup from "./LeagueGroup";
import { FAVICON_SRC } from "./icons";
import { PAGE_LIMIT, buildDateChips, todayKey } from "./model";
import "./kbv-kq-v32.css";

function Spinner() {
  return (
    <div className="flex justify-center items-center h-full">
      <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500" />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex kbv-col items-center justify-center">
      <img src={FAVICON_SRC} alt="KeoBongVip logo" className="w-[9rem] h-[7.375rem] my-4" />
      <p className="text-muted text-[0.75rem] text-center p-4">Không có trận đấu nào</p>
    </div>
  );
}

export default function KbvKetQuaLightV32() {
  const { pathname } = useLocation();
  const isHome = pathname === "/";
  const days = useMemo(() => buildDateChips(), []);
  const [date, setDate] = useState(todayKey);
  const [groups, setGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null);
  const req = useRef(0);
  const busy = useRef(false);
  const sentinelRef = useRef(null);

  const fetchPage = useCallback(async (dateStr, pageNum, append) => {
    const my = ++req.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    const result = await KbvPluginService.fetchBangKeoMatches({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateStr}`,
      filterOdds: -1,
      status: "end",
      page: pageNum,
      limit: PAGE_LIMIT,
    });
    if (my !== req.current) return;
    const payload = result?.data && !result?.error ? result.data : {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    setTotal(Number(payload.total) || 0);
    setPage(Number(payload.page) || pageNum);
    setGroups((prev) => (append ? [...prev, ...rows] : rows));
    setLoading(false);
    setLoadingMore(false);
  }, []);

  useEffect(() => {
    setGroups([]);
    setPage(1);
    setTotal(0);
    fetchPage(date, 1, false);
  }, [date, fetchPage]);

  const hasMore = page * PAGE_LIMIT < total;

  const loadMore = useCallback(() => {
    if (busy.current || loading || loadingMore || !hasMore) return;
    busy.current = true;
    fetchPage(date, page + 1, true).finally(() => {
      busy.current = false;
    });
  }, [date, fetchPage, hasMore, loading, loadingMore, page]);

  useEffect(() => {
    if (!hasMore || loading) return undefined;
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "120px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, loadMore, loading, groups.length]);

  const matchIds = useMemo(
    () =>
      groups.flatMap((group) =>
        (group?.matches || [])
          .map((match) => Number(match?.id))
          .filter((id) => Number.isFinite(id))
      ),
    [groups]
  );
  useLiveUpdates(matchIds);

  const visible = selectedIds == null
    ? groups
    : groups.filter((group) => selectedIds.has(String(group?.league?.id)));

  return (
    <div className="kbv-kq-v32 kbv-kq-v32-page w-full">
      <div className="w-full">
        <div className="mt-2">
          <div className="flex kbv-col gap-0.5 md:gap-2">
            <DateChips
              days={days}
              value={date}
              onChange={(key) => {
                if (key === date) return;
                setDate(key);
                setSelectedIds(null);
              }}
            />
            {loading ? (
              <Spinner />
            ) : (
              <div className="space-y-1 md:space-y-2 rounded p-0 bg-background-header border border-gray-200">
                {visible.length === 0 ? (
                  <EmptyState />
                ) : (
                  visible.map((group) => {
                    const league = group?.league || {};
                    const id = String(league.id);
                    return (
                      <LeagueGroup
                        key={id}
                        league={league}
                        matches={group?.matches}
                        isHome={isHome}
                      />
                    );
                  })
                )}
              </div>
            )}
            {hasMore ? (
              <div ref={sentinelRef} className="py-4 text-center">
                {loadingMore ? <Spinner /> : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

    </div>
  );
}
